import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Users, Printer, ChevronDown, ChevronRight, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../AuthContext';
import { openPrintWindow, buildPrintHTML } from '../../utils/printUtils';
import { exportToExcel } from '../../utils/excelExport';

const CrpEpReport = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(null);
  const [branch, setBranch] = useState('all');
  const [loanStatus, setLoanStatus] = useState('active');
  const [loading, setLoading] = useState(false);
  const [expandedCrpEp, setExpandedCrpEp] = useState(null);

  useEffect(() => { api.get('/branches').then(r => setBranches(r.data)).catch(() => {}); }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = { loan_status: loanStatus };
      if (branch !== 'all') params.branch_id = branch;
      const resp = await api.get('/reports/crp-ep', { params });
      setData(resp.data);
      setExpandedCrpEp(null);
    } catch { toast.error('Failed to fetch report'); }
    finally { setLoading(false); }
  };

  const getBranchName = () => branch === 'all' ? 'All Branches' : branches.find(b => b.id === branch)?.name || 'Unknown';

  const handleExcel = () => {
    if (!data || !(data.crp_eps||[]).length) { toast.error('Generate the report first'); return; }
    const rows = [];
    (data.crp_eps||[]).forEach(c => {
      (c.entrepreneurs||[]).forEach(e => {
        const eAmt = (e.loans||[]).reduce((s,l)=>s+(l.loan_amount||0),0);
        const eOs = (e.loans||[]).reduce((s,l)=>s+(l.outstanding_amount||0),0);
        rows.push({
          'CRP-EP': c.crp_ep_name || '-',
          'Branch': c.branch_name || '-',
          'Entrepreneur': e.entrepreneur_name || '-',
          'Enterprise': e.enterprise_name || '-',
          'SHG/VLF': e.shg_vlf_name || '-',
          'Loan IDs': (e.loans||[]).map(l => l.loan_id).join(', '),
          'Loan Count': (e.loans||[]).length,
          'Total Loan Amount': Math.round(eAmt),
          'Outstanding': Math.round(eOs),
        });
      });
    });
    const sm = data.summary || {};
    exportToExcel({
      filename: `WWAGS_CRP_EP_Report_${getBranchName().replace(/\s+/g,'_')}`,
      sheetName: 'CRP-EP Report',
      rows,
      kpis: [
        { label: 'Branch', value: getBranchName() },
        { label: 'Total CRP-EPs', value: sm.total_crp_eps || 0 },
        { label: 'Entrepreneurs', value: sm.total_entrepreneurs || 0 },
        { label: 'Loans', value: sm.total_loans || 0 },
        { label: 'Loan Amount', value: Math.round(sm.total_loan_amount || 0) },
        { label: 'Outstanding', value: Math.round(sm.total_outstanding || 0) },
      ],
    });
  };

  const handlePrint = () => {
    if (!data) return;
    const sm = data.summary || {};
    const crpRows = (data.crp_eps||[]).map((c, i) => {
      const entRows = (c.entrepreneurs||[]).map(e => {
        const eLoanAmt = (e.loans||[]).reduce((s,l)=>s+(l.loan_amount||0),0);
        const eOutstanding = (e.loans||[]).reduce((s,l)=>s+(l.outstanding_amount||0),0);
        return `<tr style="background:#f9f9f9"><td></td><td>${e.entrepreneur_name||'-'}</td><td>${e.enterprise_name||'-'}</td><td>${e.shg_vlf_name||'-'}</td><td style="text-align:center">${(e.loans||[]).length}</td><td style="text-align:right">Rs.${eLoanAmt.toLocaleString('en-IN')}</td><td style="text-align:right">Rs.${eOutstanding.toLocaleString('en-IN')}</td></tr>`;
      }).join('');
      return `<tr style="background:#e8f5e9;font-weight:bold"><td>${i+1}</td><td colspan="2">${c.crp_ep_name||'-'} (${c.branch_name||'-'})</td><td>Entrepreneurs: ${c.total_entrepreneurs||0}</td><td>Loans: ${c.total_loans||0}</td><td style="text-align:right">Rs.${(c.total_loan_amount||0).toLocaleString('en-IN')}</td><td style="text-align:right">Rs.${(c.total_outstanding||0).toLocaleString('en-IN')}</td></tr>${entRows}`;
    }).join('');
    const body = `<div class="cards"><div class="card"><small>Total CRP-EPs</small><b>${sm.total_crp_eps||0}</b></div><div class="card"><small>Entrepreneurs</small><b>${sm.total_entrepreneurs||0}</b></div><div class="card"><small>Loans</small><b>${sm.total_loans||0}</b></div><div class="card"><small>Loan Amount</small><b>Rs.${(sm.total_loan_amount||0).toLocaleString('en-IN')}</b></div><div class="card"><small>Outstanding</small><b>Rs.${(sm.total_outstanding||0).toLocaleString('en-IN')}</b></div></div><table><thead><tr><th>#</th><th>CRP-EP / Entrepreneur</th><th>Enterprise</th><th>SHG/VLF</th><th>Loans</th><th>Amount</th><th>Outstanding</th></tr></thead><tbody>${crpRows}</tbody></table>`;
    openPrintWindow(buildPrintHTML('CRP-EP Report', body, getBranchName()));
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="crp-ep-report-page">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/reports" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
        <div><h1 className="text-2xl font-bold text-slate-900">CRP-EP Report</h1><p className="text-sm text-slate-600">Entrepreneurs & loans grouped by CRP-EP promoter</p></div>
      </div>

      <div className="flex flex-wrap items-end gap-3 mb-6">
        {isAdmin() && <Select value={branch} onValueChange={setBranch}><SelectTrigger className="w-48"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All Branches</SelectItem>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>)}</SelectContent></Select>}
        <Select value={loanStatus} onValueChange={setLoanStatus}>
          <SelectTrigger className="w-32" data-testid="crpep-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={fetchReport} className="bg-purple-700 hover:bg-purple-800 text-white" data-testid="fetch-crp-ep">Generate</Button>
        {data && <Button variant="outline" onClick={handlePrint}><Printer className="w-4 h-4 mr-1" />Print</Button>}
        {data && <Button variant="outline" onClick={handleExcel} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="excel-btn"><FileSpreadsheet className="w-4 h-4 mr-1" />Excel</Button>}
      </div>

      {loading && <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-700"></div></div>}

      {data && !loading && (
        <div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center"><p className="text-xs text-purple-600 mb-1">CRP-EPs</p><p className="text-xl font-bold text-purple-800">{data.summary?.total_crp_eps}</p></div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center"><p className="text-xs text-blue-600 mb-1">Entrepreneurs</p><p className="text-xl font-bold text-blue-800">{data.summary?.total_entrepreneurs}</p></div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center"><p className="text-xs text-emerald-600 mb-1">Loans</p><p className="text-xl font-bold text-emerald-800">{data.summary?.total_loans}</p></div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center"><p className="text-xs text-amber-600 mb-1">Loan Amount</p><p className="text-lg font-bold text-amber-800">Rs.{(data.summary?.total_loan_amount||0).toLocaleString('en-IN')}</p></div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center"><p className="text-xs text-red-600 mb-1">Outstanding</p><p className="text-lg font-bold text-red-800">Rs.{(data.summary?.total_outstanding||0).toLocaleString('en-IN')}</p></div>
          </div>

          <div className="space-y-3">
            {(data.crp_eps||[]).map((crp, idx) => (
              <div key={crp.crp_ep_id} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 cursor-pointer hover:bg-slate-50 flex justify-between items-center" onClick={() => setExpandedCrpEp(expandedCrpEp === crp.crp_ep_id ? null : crp.crp_ep_id)}>
                  <div>
                    <span className="font-semibold text-slate-900">{idx+1}. {crp.crp_ep_name}</span>
                    <span className="ml-2 text-xs text-slate-500">({crp.branch_name}) | {crp.total_entrepreneurs} entrepreneurs | {crp.total_loans} loans</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-emerald-700">Rs.{(crp.total_loan_amount||0).toLocaleString('en-IN')}</span>
                    {expandedCrpEp === crp.crp_ep_id ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </div>
                </div>
                {expandedCrpEp === crp.crp_ep_id && (
                  <div className="border-t border-slate-200 bg-slate-50 p-4">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-purple-100"><th className="px-3 py-2 text-left">Entrepreneur</th><th className="px-3 py-2 text-left">SHG/VLF</th><th className="px-3 py-2 text-center">Loans</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-right">Outstanding</th></tr></thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {(crp.entrepreneurs||[]).map(e => (
                          <tr key={e.entrepreneur_id} className="hover:bg-slate-50">
                            <td className="px-3 py-2"><Link to={`/entrepreneurs/${e.entrepreneur_id}`} className="text-blue-700 hover:underline">{e.entrepreneur_name}</Link></td>
                            <td className="px-3 py-2 text-slate-600">{e.shg_vlf_name}</td>
                            <td className="px-3 py-2 text-center">{(e.loans||[]).map(l => <Link key={l.loan_id} to={`/loans/${l.loan_id}`} className="text-purple-600 hover:underline block">{l.loan_id}</Link>)}</td>
                            <td className="px-3 py-2 text-right">Rs.{(e.loans||[]).reduce((s,l)=>s+(l.loan_amount||0),0).toLocaleString('en-IN')}</td>
                            <td className="px-3 py-2 text-right text-red-600">Rs.{(e.loans||[]).reduce((s,l)=>s+(l.outstanding_amount||0),0).toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {!data && !loading && <div className="text-center py-12 text-slate-500"><Users className="w-12 h-12 mx-auto mb-3 text-slate-300" /><p>Click "Generate" to view CRP-EP report</p></div>}
    </div>
  );
};

export default CrpEpReport;
